/**
 * EventDetectionPanel - UI for triggering event detection with LLM options
 * 
 * Features:
 * - Toggle between rule-based and LLM-enhanced classification
 * - Configure context (aircraft type, flight phase, scenario)
 * - Display detection results and statistics (persistent across page visits)
 * - Show LLM reasoning for classified events
 */

import { useState, useEffect } from 'react';
import { useEventDetection } from '../hooks/useEventDetection';
import { Search, Sparkles, AlertCircle, CheckCircle, Loader2, Info, RefreshCw } from 'lucide-react';
import { FLIGHT_PHASE_OPTIONS } from '../lib/constants';
import { supabase } from '../lib/supabase';

interface ExistingEventsState {
  count: number;
  statistics: {
    critical: number;
    warning: number;
    positive: number;
    info: number;
  };
  lastDetectedAt: string | null;
}

interface EventDetectionPanelProps {
  sessionId: string;
  transcriptId: string;
  onComplete?: () => void;
  // Context from session details
  aircraftType?: string;
  flightPhase?: string;
  scenario?: string;
}

export default function EventDetectionPanel({
  sessionId,
  transcriptId,
  onComplete,
  aircraftType: initialAircraftType,
  flightPhase: initialFlightPhase,
  scenario: initialScenario,
}: EventDetectionPanelProps) {
  const { detectEvents, loading, error, result, progress } = useEventDetection();

  // LLM options state - initialized from session details (LLM enabled by default)
  const [useLLM, setUseLLM] = useState(true);
  const [aircraftType, setAircraftType] = useState(initialAircraftType || '');
  const [flightPhase, setFlightPhase] = useState(initialFlightPhase || '');
  const [scenario, setScenario] = useState(initialScenario || '');

  // State for existing events (persistent display)
  const [existingEvents, setExistingEvents] = useState<ExistingEventsState | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  // Fetch existing events on mount
  useEffect(() => {
    const fetchExistingEvents = async () => {
      setLoadingExisting(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('events')
          .select('id, severity, created_at')
          .eq('session_id', sessionId)
          .eq('dismissed', false)
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error('[EventDetectionPanel] Error fetching existing events:', fetchError);
          setExistingEvents(null);
        } else if (data && data.length > 0) {
          setExistingEvents({
            count: data.length,
            statistics: {
              critical: data.filter(e => e.severity === 'critical').length,
              warning: data.filter(e => e.severity === 'warning').length,
              positive: data.filter(e => e.severity === 'positive').length,
              info: data.filter(e => e.severity === 'info').length,
            },
            lastDetectedAt: data[0]?.created_at || null,
          });
        } else {
          setExistingEvents(null);
        }
      } catch (err) {
        console.error('[EventDetectionPanel] Error fetching existing events:', err);
        setExistingEvents(null);
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExistingEvents();
  }, [sessionId]);

  // Update existing events after successful detection
  useEffect(() => {
    if (result && result.statistics) {
      setExistingEvents({
        count: result.eventsDetected,
        statistics: {
          critical: result.statistics.critical,
          warning: result.statistics.warning,
          positive: result.statistics.positive,
          info: result.statistics.info,
        },
        lastDetectedAt: new Date().toISOString(),
      });
    }
  }, [result]);

  const handleDetectEvents = async () => {
    const options = useLLM
      ? {
          useLLM: true,
          aircraftType: aircraftType || undefined,
          flightPhase: flightPhase || undefined,
          scenario: scenario || undefined,
        }
      : undefined;

    const detectionResult = await detectEvents(sessionId, transcriptId, options);

    if (detectionResult && onComplete) {
      onComplete();
    }
  };

  return (
    <div className="space-y-3">
      {/* Header - compact */}
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-orca-elevate flex items-center justify-center flex-shrink-0">
          <Search className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-navy">Event Detection</h3>
          <p className="text-xs text-steel">
            Detect aviation events and map them to ICAO 9995 competencies
          </p>
        </div>
      </div>

      {/* Event counts - single line with colors */}
      {loadingExisting ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading event status...
        </div>
      ) : existingEvents ? (
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
          <span className="flex items-center gap-1.5 text-sm font-medium text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full">
            <CheckCircle className="w-3.5 h-3.5" />
            {existingEvents.count} events detected
          </span>
          <span className="text-sm font-semibold text-red-600">{existingEvents.statistics.critical} Critical</span>
          <span className="text-sm font-semibold text-yellow-600">{existingEvents.statistics.warning} Warning</span>
          <span className="text-sm font-semibold text-green-600">{existingEvents.statistics.positive} Positive</span>
          <span className="text-sm font-semibold text-blue-600">{existingEvents.statistics.info} Info</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Info className="w-4 h-4 text-gray-400" />
          No events detected yet. Run event detection to identify aviation events.
        </div>
      )}

      {/* LLM Toggle - compact */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="useLLM"
            checked={useLLM}
            onChange={(e) => setUseLLM(e.target.checked)}
            className="mt-0.5 h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
            disabled={loading}
          />
          <div className="flex-1">
            <label htmlFor="useLLM" className="flex items-center gap-1.5 text-sm font-medium text-gray-900 cursor-pointer">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              Enable LLM-Enhanced Classification
              <span className="text-xs font-normal text-gray-500">(~2-5s per event, $0.002-0.005 per event)</span>
            </label>
            <p className="text-xs text-gray-500 mt-0.5">
              Use AI to refine competency mapping with context-aware classification
            </p>
          </div>
        </div>

        {/* Context Options (shown when LLM is enabled) */}
        {useLLM && (
          <div className="ml-6 grid grid-cols-3 gap-2 pt-2 border-t border-gray-200">
            {/* Aircraft Type */}
            <div>
              <label htmlFor="aircraftType" className="block text-xs font-medium text-gray-600 mb-0.5">
                Aircraft Type
              </label>
              <input
                type="text"
                id="aircraftType"
                value={aircraftType}
                onChange={(e) => setAircraftType(e.target.value)}
                placeholder="e.g., A320"
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                disabled={loading}
              />
            </div>

            {/* Flight Phase */}
            <div>
              <label htmlFor="flightPhase" className="block text-xs font-medium text-gray-600 mb-0.5">
                Flight Phase
              </label>
              <select
                id="flightPhase"
                value={flightPhase}
                onChange={(e) => setFlightPhase(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                disabled={loading}
              >
                <option value="">Select...</option>
                {FLIGHT_PHASE_OPTIONS.map(phase => (
                  <option key={phase} value={phase}>{phase}</option>
                ))}
              </select>
            </div>

            {/* Scenario */}
            <div>
              <label htmlFor="scenario" className="block text-xs font-medium text-gray-600 mb-0.5">
                Scenario
              </label>
              <select
                id="scenario"
                value={scenario}
                onChange={(e) => setScenario(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                disabled={loading}
              >
                <option value="">Select...</option>
                <option value="training">Training</option>
                <option value="line check">Line Check</option>
                <option value="proficiency check">Proficiency Check</option>
                <option value="line operations">Line Operations</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Detect Button - blue color */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleDetectEvents();
        }}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold transition-all text-sm"
        style={{
          backgroundColor: loading ? '#d1d5db' : '#0284c7',
          color: loading ? '#6b7280' : '#ffffff',
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {progress === 0 ? 'Starting...' :
             progress <= 20 ? `Loading data... ${progress}%` :
             progress <= 40 ? `Scanning transcript... ${progress}%` :
             progress <= 80 ? `AI classification... ${progress}%` :
             progress < 100 ? `Saving events... ${progress}%` :
             'Finishing...'}
          </>
        ) : existingEvents ? (
          <>
            <RefreshCw className="w-4 h-4" />
            Re-detect Events {useLLM && '(AI-Enhanced)'}
          </>
        ) : (
          <>
            {useLLM ? <Sparkles className="w-4 h-4" /> : <Search className="w-4 h-4" />}
            Detect Events {useLLM && '(AI-Enhanced)'}
          </>
        )}
      </button>

      {/* Progress bar during detection */}
      {loading && (
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-sky-500 h-1.5 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${Math.max(progress, 2)}%` }}
          />
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm text-red-900">Detection Failed</p>
            <p className="text-xs text-red-700 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Success Display - compact */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="font-medium text-sm text-green-900">
              Successfully detected {result.eventsDetected} events
            </p>
          </div>
        </div>
      )}

      {/* Info Box - keep at end */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-2.5 text-xs">
        <div className="flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 text-sky-600 flex-shrink-0 mt-0.5" />
          <div className="text-sky-900">
            <p className="font-medium text-xs">How it works:</p>
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-sky-800">
              <li><strong>Rule-based:</strong> Fast keyword matching (~100-500ms)</li>
              {useLLM && (
                <>
                  <li><strong>LLM-enhanced:</strong> AI refines competency mapping with context (~2-5s per event)</li>
                  <li><strong>Hybrid approach:</strong> Combines both methods for best accuracy</li>
                  <li><strong>Context-aware:</strong> Considers aircraft type, flight phase, and scenario</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

